// F15: how to install the TypeScript packages, in one place. npm has 0.2.0 of both (no assistant); the current
// versions are packed by the release build (scripts/pack-packages.ts) and served from the developer hub, so
// every install line points there until they are on npm. Once `npm publish` has run for the version below, set
// `onNpm: true` and run `npm run docs:llms`: the hub, llms.txt, the quickstarts, the builders page, the portal
// and the in-app assistant all read their install line from here (tests/developer-docs.test.ts checks it).
// No Node imports: the portal bundles this file too.

export const DEVELOPERS_HUB = 'https://developers.outlevo.com';

export type PackageId = 'sdk' | 'components';
type Pkg = { name: string; version: string; onNpm: boolean; npmVersion: string };

/** `version` matches packages/<id>/package.json (tested); `npmVersion` is the newest version published to npm. */
export const PACKAGES: Record<PackageId, Pkg> = {
  sdk: { name: '@outlevo/sdk', version: '0.3.0', onNpm: false, npmVersion: '0.2.0' },
  components: { name: '@outlevo/components', version: '0.3.0', onNpm: false, npmVersion: '0.2.0' },
};

/** The file `npm pack` writes for a package: @outlevo/sdk 0.3.0 → outlevo-sdk-0.3.0.tgz */
export const tarballFile = (id: PackageId) => `${PACKAGES[id].name.replace(/^@/, '').replace('/', '-')}-${PACKAGES[id].version}.tgz`;
export const tarballUrl = (id: PackageId) => `${DEVELOPERS_HUB}/${tarballFile(id)}`;
export const installCommand = (id: PackageId) => `npm install ${PACKAGES[id].onNpm ? PACKAGES[id].name : tarballUrl(id)}`;

/** One plain sentence on where the packages come from; empty once both are on npm. */
export function npmNote(): string {
  const off = (Object.keys(PACKAGES) as PackageId[]).filter((id) => !PACKAGES[id].onNpm);
  if (!off.length) return '';
  const names = off.map((id) => PACKAGES[id].name).join(' and ');
  const v = PACKAGES[off[0]!];
  return `Version ${v.version} of ${names} is coming to npm; until then it installs from the developer hub, and the import is the same. npm still has ${v.npmVersion}, without the assistant.`;
}

/** Matches any install line this file has produced, so the docs can be rewritten when it changes. */
export const installPattern = (id: PackageId) => new RegExp(`npm install (?:${PACKAGES[id].name.replace('/', '\\/')}|${DEVELOPERS_HUB.replace(/[.]/g, '\\.')}\\/${PACKAGES[id].name.replace(/^@/, '').replace('/', '-')}-\\d+\\.\\d+\\.\\d+\\.tgz)(?![\\w./-])`, 'g');

/** The "Libraries" line of developers/llms.txt. */
export const librariesLine = () => `- Libraries: \`${installCommand('sdk')}\` (TypeScript client for servers: Node 18+, Bun, Deno, edge) and \`${installCommand('components')}\` (React and Web Components, fed through your own server route).${npmNote() ? ` ${npmNote()}` : ''} Any other language calls the REST API directly; PHP/Laravel examples are below.`;

/** Rewrites every install line in a document to the current one (and the llms.txt "Libraries" line). */
export function syncInstalls(text: string): string {
  let out = text.replace(installPattern('sdk'), installCommand('sdk')).replace(installPattern('components'), installCommand('components'));
  out = out.replace(/^- Libraries\b.*$/m, librariesLine());
  return out;
}
